/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectionManagementWizard/rejectionManagementWizard"/>
import * as React from "react";
import { Button, ButtonGroup, DialogPart, FlexLayout, FlexSpacer, InstantSearchBox, LocalizationContext, ModalDialogShell, StringMap, Text } from "@medius/ui-controls";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import ReasonCodesGrid from "../shared/reasonCodesGrid";
import { postISupplierInvoiceRejectionDto, getSelectedReasonCodes, getEmailEligibility } from "./services";
import { IRecipientAddress, IReasonCode, ISupplierInvoiceRejectionDto, ReasonCodeType, RejectionType} from "./interfaces";
import { EntityType } from "Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/shared/interfaces";
import { handleError } from "Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler";
import { RejectionManagementEmailWizard } from "./rejectionManagementEmailWizard";
import { EmailAttachmentItem } from "./emailAttachmentsDropzone";
import { useInvoiceReasonCodeData } from "./useInvoiceReasonCodeData";
import { mapFromNorthstarToApa } from "Core/Medius.Core.Web/Scripts/lib/northstarLocalization";
import { getCultureCode } from "Core/Medius.Core.Web/Scripts/Medius/lib/utils/user";
import { useRejectionManagementEmailForm } from "./useRejectionManagementEmailForm";
import { FieldValues } from "react-hook-form";
import { ReclassifyTypeModal } from "./reclassifyTypeModal";
import { RiskFactorReasonCode, RiskFactorsForInvoice } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/RiskFactor/RiskFactorComponentDtos/riskFactorsForInvoiceDto";
import { saveRiskDetails } from "../../../fraudPopup/service";
import { isEmailSendingInRejectionManagementEnabled } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";
import { ReasonCodeOperationType } from "../shared/interfaces";
import { getTranslatedDocumentType } from "../shared/utils";

export interface IRejectionWizardCloseEvent {
    wasConfirmed?: boolean, 
    targetDocumentType?: string, 
    wasEmailSent?: boolean,
    riskFactorReasonCode?: string;
}

export interface RejectionManagementWizardProps {
    wizardType: ReasonCodeOperationType;
    documentId: number;
    hierarchyId: string;
    task: string;
    rejectedEntityType: EntityType;
    onClose: (closeEventDetails: IRejectionWizardCloseEvent) => void;
    possibleReclassifyTypesList?: string[];
    currentDocumentType?: string;
    isFraudAndRiskProtectionEnabled?: boolean;
    riskDetails?: RiskFactorsForInvoice;
    taskId?: number;
}

enum RejectionManagementWizardStep {
    ReasonCode,
    Email,
    ReclassifyType
}

export const RejectionManagementWizard: React.FC<RejectionManagementWizardProps> = ({
    wizardType,
    documentId,
    hierarchyId,
    task,
    rejectedEntityType,
    onClose,
    possibleReclassifyTypesList,
    currentDocumentType,
    isFraudAndRiskProtectionEnabled,
    riskDetails,
    taskId
}) => {
    const searchLabelId = "searchLabelId";
    const [selection, setSelection] = React.useState<IReasonCode[]>([]);
    const [step, setStep] = React.useState<RejectionManagementWizardStep>(RejectionManagementWizardStep.ReasonCode);
    const [targetDocumentType, setTargetDocumentType] = React.useState<string>(null);
    const [isEligibleForEmail, setIsEligibleForEmail] = React.useState<boolean | null>(null);
    const formContext = useRejectionManagementEmailForm();
    const { handleSubmit } = formContext;
    const isSelectionMandatory = selection?.length === 0;

    const getWizardTitle = React.useCallback((): string => {
        switch (wizardType) {
            case ReasonCodeOperationType.Delete:
                return translate("#PurchaseToPay/documentReasonCodes/deleteInvoice");
            case ReasonCodeOperationType.Invalidate:
                return translate("#PurchaseToPay/documentReasonCodes/invalidateInvoice");
            case ReasonCodeOperationType.InvalidateAndEdit:
                return translate("#PurchaseToPay/documentReasonCodes/invalidateAndEditInvoice");
        }
    }, [wizardType]);

    const hasExternalType = selection.some(rc => Array.isArray(rc.types) && rc.types.includes(ReasonCodeType.External));

    const getButtonLabel = React.useCallback((): string => {
        if (step === RejectionManagementWizardStep.Email) {
            switch (wizardType) {
                case ReasonCodeOperationType.Delete:
                    return translate("#Core/deleteIt");
                case ReasonCodeOperationType.Invalidate:
                    return translate("#Core/invalidateIt");
                case ReasonCodeOperationType.InvalidateAndEdit:
                    return translate("#Core/next");
            }
        }
        if (step === RejectionManagementWizardStep.ReclassifyType) {
            return translate("#Core/registerDocument");
        }

        if (hasExternalType && isEmailSendingInRejectionManagementEnabled() && isEligibleForEmail === true) 
            return translate("#Core/next");

        switch (wizardType) {
            case ReasonCodeOperationType.Delete:
                return translate("#Core/deleteIt");
            case ReasonCodeOperationType.Invalidate:
                return translate("#Core/invalidateIt");
            case ReasonCodeOperationType.InvalidateAndEdit:
                return translate("#Core/next");
        }
    }, [wizardType, hasExternalType, step, isEligibleForEmail]);

    const getModalTitle = React.useCallback((): string => {
        if (step === RejectionManagementWizardStep.Email) {
            return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendEmailToSupplier");
        }
        if (step === RejectionManagementWizardStep.ReclassifyType) {
            return translate("#Core/chooseReclassifyDocumentType");
        }
        return translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/selectReasonCode");
    }, [step]);

    const invoiceReasonCodeData = useInvoiceReasonCodeData(wizardType, hierarchyId);
    const { searching: { changeSearchTerm: setSearch } } = invoiceReasonCodeData;

    const executeRejectionFlow = async (formData: FieldValues) => {
        try {
            const sendEmailInformation = shouldSendEmailInformation(formData);
            const supplierInvoiceRejectionDto: ISupplierInvoiceRejectionDto = {
                documentId: documentId,
                documentType: getTranslatedDocumentType(currentDocumentType),
                operation: wizardType === ReasonCodeOperationType.Delete ? translate("#Core/delete") : translate("#Enterprise/invalidate"),
                task: task,
                rejectedEntityType: rejectedEntityType,
                rejectionType: wizardType === ReasonCodeOperationType.Delete 
                    ? RejectionType.Delete 
                    : RejectionType.Invalidate,
                invoiceRejectionReasonItems: selection.map(rc => ({
                    reasonCodeId: rc.id,
                    actionId: rc.rejectionActionId
                })),
                hasExternalReasonCode: hasExternalType,
                sendToSupplier: sendEmailInformation,
                ...(sendEmailInformation && {
                    invoiceRejectionEmail: {
                        emailTo: formData.emailTo.filter((ra: IRecipientAddress) => ra.value.trim() !== ""),
                        carbonCopy: formData.carbonCopy.filter((ra: IRecipientAddress) => ra.value.trim() !== ""),
                        sendCopy: formData.sendCopy,
                        messageSubject: formData.messageSubject,
                        content: formData.content,
                    }
                })
            };

            const attachments = sendEmailInformation
                ? ((formData.attachments ?? []) as EmailAttachmentItem[])
                    .filter(item => !item.errorMessage && item.progressStatus !== "failed")
                    .map(item => item.file)
                : undefined;
            await postISupplierInvoiceRejectionDto(supplierInvoiceRejectionDto, attachments);

            if (isFraudAndRiskProtectionEnabled) {
                const riskDetailsDto = getRiskDetails(selection);
                await saveRiskDetails(taskId, riskDetailsDto);
            }

            const actualEmailSent = sendEmailInformation && isEligibleForEmail;

            onClose({wasConfirmed: true, targetDocumentType, wasEmailSent: actualEmailSent, riskFactorReasonCode: getRiskFactorReasonCode(selection)}); // confirmed
        } catch (e) {
            handleError(e);
        }
    };

    const shouldSendEmailInformation = (formData: FieldValues): boolean => {
        if (isEligibleForEmail === false) {
            return false;
        }
        if (step === RejectionManagementWizardStep.Email) {
            return formData.sendToSupplier;
        }
        if (step === RejectionManagementWizardStep.ReclassifyType && hasExternalType && isEmailSendingInRejectionManagementEnabled()) {
            return formData.sendToSupplier;
        }
        return false;
    };

    const getPrioritizedSystemReasonCode = (selection: IReasonCode[]) => {
        const systemReasonCodeOrder = ["FraudAttempt", "DuplicateInvoice", "NotAnInvoice", "Other"];
        const sorted = [...selection].sort((a, b) => {
            const aIndex = systemReasonCodeOrder.indexOf(a.systemReasonCode);
            const bIndex = systemReasonCodeOrder.indexOf(b.systemReasonCode);
            const safeA = aIndex === -1 ? systemReasonCodeOrder.length : aIndex;
            const safeB = bIndex === -1 ? systemReasonCodeOrder.length : bIndex;
            return safeA - safeB;
        });
        const prioritizedSystemReasonCode = sorted.length > 0 ? sorted[0] : null;

        return prioritizedSystemReasonCode?.systemReasonCode;
    };

    const getRiskFactorReasonCode = (selection: IReasonCode[]): RiskFactorReasonCode | null => {
        if(selection === null || selection.length === 0){
            return null;
        }

        const systemReasonCode = getPrioritizedSystemReasonCode(selection);
        switch (systemReasonCode) {
            case "FraudAttempt":
                return RiskFactorReasonCode.InvoiceDeletedFraudAttempt;
            case "DuplicateInvoice":
                return RiskFactorReasonCode.InvoiceDeletedDuplicateInvoice;
            case "NotAnInvoice":
                return RiskFactorReasonCode.InvoiceDeletedNotAnInvoice;
            case "Other":
                return RiskFactorReasonCode.InvoiceDeletedOther;
            default:
                return null;
        }
    };

    const getRiskDetails = (selection: IReasonCode[]) => {
        const reason = getRiskFactorReasonCode(selection);
        if(reason == null){
            throw new Error("No valid system reason code found");
        }

        riskDetails.riskFactors = riskDetails.riskFactors.map(rf => ({
            ...rf,
            handledInfo: {
                ...rf.handledInfo,
                reasonCode: reason,
            }
        }));

        const riskDetailsDto = riskDetails.riskFactors.map(rf => (
            {
                RiskFactorCode: rf.code,
                HandlingReason: reason
            }
        ));
        return riskDetailsDto;
    };

    const handleDelete = handleSubmit(async (formData: FieldValues) => {

        if (step === RejectionManagementWizardStep.ReclassifyType) {
            await executeRejectionFlow(formData);
            return;
        }

        if (step === RejectionManagementWizardStep.Email) {
            if (wizardType === ReasonCodeOperationType.InvalidateAndEdit)
                setStep(RejectionManagementWizardStep.ReclassifyType);
            else
                await executeRejectionFlow(formData);
            return;
        }

        if (hasExternalType && isEmailSendingInRejectionManagementEnabled()) {
            if (isEligibleForEmail === null) {
                if (isEligibleForEmail) {
                    setStep(RejectionManagementWizardStep.Email);
                    return;
                }
            } else if (isEligibleForEmail === true) {
                setStep(RejectionManagementWizardStep.Email);
                return;
            }
        }

        if (wizardType === ReasonCodeOperationType.InvalidateAndEdit) {
            setStep(RejectionManagementWizardStep.ReclassifyType);
            return;
        }

        await executeRejectionFlow(formData);
    });

    const calculateBackState = (): RejectionManagementWizardStep => {
        if (step === RejectionManagementWizardStep.ReclassifyType) {
            if (hasExternalType && isEmailSendingInRejectionManagementEnabled() && isEligibleForEmail === true)
                return RejectionManagementWizardStep.Email;
        }
        return RejectionManagementWizardStep.ReasonCode;
    };

    React.useEffect(() => {
        let isCancelled = false;

        // initiating reason codes and email communication eligibility
        (async () => {
            try {
                const [selectedReasonCodes, eligibleForEmailCommunication] = await Promise.all([
                    getSelectedReasonCodes(documentId),
                    getEmailEligibility(documentId),
                ]);

                if (isCancelled) return;

                setSelection(selectedReasonCodes);
                setIsEligibleForEmail(eligibleForEmailCommunication?.isEligible ?? false);
            } catch (e) {
                if (!isCancelled) {
                    setIsEligibleForEmail(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    },[documentId]);


    React.useEffect(() => {
        if (currentDocumentType)
            setTargetDocumentType(currentDocumentType);
    }, [currentDocumentType]);

    return (
        <LocalizationContext.Provider
            value={{
                translate: (key: string, _, defaultValue?: string, args?: StringMap) =>
                    mapFromNorthstarToApa(key, defaultValue, args),
                getLocale: () => getCultureCode()
            }}
        >
            <ModalDialogShell
                isOpen
                hasHeader
                height={step === RejectionManagementWizardStep.ReclassifyType ? 'content' : 'vlarge'}
                title={getWizardTitle()}
                data-testid="rejection-management-wizard"
                onRequestClose={() => onClose({wasConfirmed: false})} >
                {dialogProps => <>
                    <DialogPart.Title {...dialogProps.headerProps} />
                    <DialogPart.Content width={step === RejectionManagementWizardStep.ReasonCode ? "vlarge" : "large"}>
                        <div className="rejection-management-wizard">
                            <div className="rejection-management-wizard__header">
                                <Text id={searchLabelId} variant="heading3">{getModalTitle()}</Text>
                                {step === RejectionManagementWizardStep.ReasonCode && (
                                    <InstantSearchBox
                                        data-testid="rejection-management-wizard-search-box"
                                        labelId={searchLabelId}
                                        width="fill"
                                        spacerMode="on"
                                        executeSearch={(searchTerm) => { setSearch(searchTerm); }} />
                                )}
                            </div>
                            <div className="rejection-management-wizard__content">
                                {step === RejectionManagementWizardStep.Email && hasExternalType && isEligibleForEmail === true && (
                                    <RejectionManagementEmailWizard
                                        formContext={formContext}
                                        documentId={documentId}
                                        supplierEmailForRejections={invoiceReasonCodeData.companyConfiguration.supplierEmailForRejections.value}
                                        selection={selection}
                                        rejectionActions={invoiceReasonCodeData.actions}
                                    />
                                )}
                                {step === RejectionManagementWizardStep.ReasonCode && (
                                    <ReasonCodesGrid
                                        selection={selection}
                                        setSelection={setSelection}
                                        invoiceReasonCodeData={invoiceReasonCodeData}
                                    />
                                )}
                                {step === RejectionManagementWizardStep.ReclassifyType && (
                                    <ReclassifyTypeModal
                                        possibleReclassifyTypeList={possibleReclassifyTypesList}
                                        targetDocumentType={targetDocumentType}
                                        setTargetDocumentType={setTargetDocumentType}
                                    />
                                )}
                            </div>
                        </div>
                    </DialogPart.Content>
                    <DialogPart.Footer>
                        <FlexLayout>
                            {(step !== RejectionManagementWizardStep.ReasonCode) && (
                                <Button
                                    data-testid="rejection-wizard-back-button"
                                    label={translate("#Core/back")}
                                    onClick={() =>
                                        setStep(calculateBackState())
                                    }
                                    variant="inline"
                                />
                            )}
                            <FlexSpacer />
                            <ButtonGroup>
                                <Button
                                    data-testid="rejection-wizard-cancel-button"
                                    label={translate("#Core/cancel")}
                                    variant="secondary"
                                    onClick={() => onClose({wasConfirmed: false})} />
                                <Button
                                    data-testid="rejection-wizard-delete-it-button"
                                    label={getButtonLabel()}
                                    onClick={handleDelete}
                                    disabled={isSelectionMandatory}
                                />
                            </ButtonGroup>
                        </FlexLayout>
                    </DialogPart.Footer>
                </>}
            </ModalDialogShell>
        </LocalizationContext.Provider>
    );
};